
import {type HostedFieldsHandler, type HostedFieldsEvent, type PayPalHostedFieldsComponent, type HostedFieldsHostedFieldsFieldData, type PayPalHostedFieldsComponentOptions, type PayPalNamespace, type OnApproveActions, type OnApproveData} from '@paypal/paypal-js';
import {$qs} from '../../../@shared/ts/dom';
import {maybeFetchWP} from '../../../@shared/ts/maybe';
import {FeatureFlag} from '../../../@type/features';
import {getErrorString} from '../../../@shared/ts/error';
import {type PayPalPaymentData} from '../../../express-checkout/ts/payment/paypal/paypal';
import {getLocaleText} from '../../../express-checkout/ts/util/translation';
import {type PaymentAttempt} from '../../../@shared/ts/paymentAttempt';
import {type IResponse} from '../../../@type/response';

// PayPal types from the "@paypal/paypal-js" library are incorrect/out of date so we need to override a few of them here.
export type HostedFieldRender = (targetContainer: string | HTMLElement) => void;
type HostedField = (HostedFieldsHandler & {render: HostedFieldRender});
type HostedFieldsComponent = (PayPalHostedFieldsComponent & {
	NameField: (options: unknown) => HostedField;
	NumberField: (options: unknown) => HostedField;
	ExpiryField: (options: unknown) => HostedField;
	CVVField: (options: unknown) => HostedField;
	submit: () => Promise<unknown>;
});
type PayPalInstance = PayPalNamespace & {
	CardFields?: (
		options?: PayPalHostedFieldsComponentOptions & {onApprove: (data: OnApproveData, actions: OnApproveActions) => void}
	) => HostedFieldsComponent;
};
type HostedFieldEvent = HostedFieldsEvent & {isFormValid: boolean} & {fields: {cardNumberField?: HostedFieldsHostedFieldsFieldData; cardExpiryField?: HostedFieldsHostedFieldsFieldData; cardCvvField?: HostedFieldsHostedFieldsFieldData}};

type Capture = {
	id: string;
	status: string;
	processor_response: {
		avs_code: string;
		cvv_code: string;
		response_code: string;
	};
};
type PurchaseUnits = {
	payments: {
		captures: Capture[];
	};
};
type Order = {
	id: string;
	status: string;
	purchase_units: [PurchaseUnits];
};

class PayPalCardPayment {
	private readonly paypal: PayPalInstance | null;
	private readonly cardFields: HostedFieldsComponent | null;

	private numberField: HostedField | null;
	private nameField: HostedField | null;
	private expiryField: HostedField | null;
	private cvvField: HostedField | null;

	private paymentAttempt: PaymentAttempt | null;

	public constructor(paypal: PayPalInstance) {
		this.paypal = paypal;
		if (!this.paypal) {
			throw new Error('PayPal instance is not valid');
		}

		// Since JS is funky and the "this" value is relative inside arrow functions we assign it to a variable which is captured in the following callbacks
		const cardPayment = this;

		// eslint-disable-next-line new-cap
		this.cardFields = this.paypal.CardFields?.({
			createOrder: async () => cardPayment.createOrder(),
			onApprove: async data => cardPayment.onApprove(data),
			onError: async error => cardPayment.onError(error),
		}) ?? null;

		this.nameField = null;
		this.numberField = null;
		this.expiryField = null;
		this.cvvField = null;

		this.paymentAttempt = null;
	}

	public gatewayId() {
		return 'peachpay_paypal_card';
	}

	public isEligible() {
		return true;
		// return this.cardFields?.isEligible() ?? false;
	}

	public isMounted() {
		return $qs('#pp-paypal-card-element')?.dataset['isMounted'] === 'true';
	}

	public mount() {
		if (!this.isEligible()) {
			throw new Error(`${this.gatewayId()} is not eligible for this checkout. Element not mounted.`);
		}

		// Since JS is funky and the "this" value is relative inside arrow functions we assign it to a variable which is captured in the following callbacks
		const cardPayment = this;

		const inputEvents = {
			onFocus(data: HostedFieldEvent) {
				cardPayment.onFocus(data);
			},
			onChange(data: HostedFieldEvent) {
				cardPayment.onChange(data);
			},
			onBlur(data: HostedFieldEvent) {
				cardPayment.onBlur(data);
			},
			onInputSubmitRequest(data: HostedFieldEvent) {
				cardPayment.onInputSubmitRequest(data);
			},
		};

		// eslint-disable-next-line new-cap
		this.nameField = this.cardFields?.NameField({inputEvents}) ?? null;
		const nameFieldContainer = $qs('#pp-paypal-card-name');
		if (this.nameField && nameFieldContainer) {
			nameFieldContainer.innerHTML = '';
			this.nameField.render(nameFieldContainer);
		}

		// eslint-disable-next-line new-cap
		this.numberField = this.cardFields?.NumberField({inputEvents}) ?? null;
		const numberFieldContainer = $qs('#pp-paypal-card-number');
		if (this.numberField && numberFieldContainer) {
			numberFieldContainer.innerHTML = '';
			this.numberField.render(numberFieldContainer);
		}

		// eslint-disable-next-line new-cap
		this.expiryField = this.cardFields?.ExpiryField({inputEvents}) ?? null;
		const expiryFieldContainer = $qs('#pp-paypal-card-expiry');
		if (this.expiryField && expiryFieldContainer) {
			expiryFieldContainer.innerHTML = '';
			this.expiryField.render(expiryFieldContainer);
		}

		// eslint-disable-next-line new-cap
		this.cvvField = this.cardFields?.CVVField({inputEvents}) ?? null;
		const cvvFieldContainer = $qs('#pp-paypal-card-cvv');
		if (this.cvvField && cvvFieldContainer) {
			cvvFieldContainer.innerHTML = '';
			this.cvvField.render(cvvFieldContainer);
		}

		$qs('#pp-paypal-card-element')?.setAttribute('data-is-mounted', 'true');
	}

	/**
     * Used to submit the checkout for PayPal card payments.
     */
	public submit(paymentAttempt: PaymentAttempt) {
		this.paymentAttempt = paymentAttempt;

		if (!this.isMounted()) {
			paymentAttempt.setPaymentMessage(getLocaleText('PayPal card fields are not ready yet. Please wait a moment and try again.'));
			paymentAttempt.stopLoading();
			return;
		}

		paymentAttempt.createTransaction(this.gatewayId())
			.then(async () => this.cardFields?.submit()/* If card submitting(Tokenizing) is successful then the method "createOrder" is called */)
			.catch(async (error: {message?: string}) => {
				const message = this.getFriendlyErrorMessage(error);

				await paymentAttempt.completeTransaction({note: message});
				paymentAttempt.setPaymentMessage(message);
				paymentAttempt.stopLoading();
			});
	}

	/**
     * Called by PayPal after a customer requests to submit the payment. This method is responsible
     * for creating the paypal order.
     */
	private async createOrder() {
		const paymentAttempt = this.paymentAttempt!;

		try {
			const result = await paymentAttempt.submitOrder<PayPalPaymentData>('paypal');
			const paypalOrderId = result?.data?.id ?? '';
			return paypalOrderId;
		} catch (error: unknown) {
			await paymentAttempt.completeTransaction({
				paymentStatus: 'unperformed',
				orderStatus: 'creationfailed',
				note: getErrorString(error),
			});

			paymentAttempt.setPaymentMessage(getLocaleText('An error occurred while creating your order. Please try again.'));
			paymentAttempt.stopLoading();

			return '';
		}
	}

	/**
     * Called by PayPal after the customer successfully completes any prompts like 3D secure.
     * This method is responsible for capturing the order payment.
     */
	private async onApprove(_data: {orderID: string}) {
		const paymentAttempt = this.paymentAttempt!;

		const approveURL = paymentAttempt.featureMetadata(FeatureFlag.PAYPAL_GATEWAYS, 'approve_order_url');
		if (!approveURL) {
			const errorMessage = getLocaleText('Missing required dependencies to Capture/Confirm PayPal order.');

			await paymentAttempt.completeTransaction({note: errorMessage});
			paymentAttempt.setPaymentMessage(errorMessage);
			paymentAttempt.stopLoading();

			return;
		}

		const orderId = paymentAttempt.getOrderId();
		const formData = new FormData();
		formData.append('order_id', orderId);

		const {result, error} = await maybeFetchWP<IResponse<Order>>(approveURL, {
			method: 'POST',
			body: formData,
		});

		if (error || !result || !result.data?.purchase_units?.[0]?.payments?.captures?.[0]?.status) {
			const errorMessage = getLocaleText('An error occurred while capturing your order. Please try again.');

			await paymentAttempt.completeTransaction({note: errorMessage});
			paymentAttempt.setPaymentMessage(errorMessage);
			paymentAttempt.stopLoading();
			return;
		}

		if (result.data.purchase_units[0].payments.captures[0].status !== 'COMPLETED') {
			let errorMessage = getLocaleText('An error occurred while capturing your order. Please try again.');

			switch (result.data.purchase_units[0].payments.captures[0].processor_response.response_code) {
				case '9500':
					errorMessage = getLocaleText('Fraudulent card');
					break;
				case '5400':
					errorMessage = getLocaleText('Card expired');
					break;
				case '5180':
					errorMessage = getLocaleText('Luhn Check fails');
					break;
				case '5120':
					errorMessage = getLocaleText('Insufficient funds');
					break;
				case '9520':
					errorMessage = getLocaleText('Card lost, stolen');
					break;
				case '1330':
					errorMessage = getLocaleText('Card not valid');
					break;
				case '5100':
				case '0500':
					errorMessage = getLocaleText('Card is declined');
					break;
				case '00N7':
					errorMessage = getLocaleText('CVC check fails');
					break;
			}

			await paymentAttempt.completeTransaction({note: errorMessage});
			paymentAttempt.setPaymentMessage(errorMessage);
			paymentAttempt.stopLoading();

			return;
		}

		paymentAttempt.redirectSuccess();
	}

	/**
     * Called by PayPal after any API errors. This method can be used for presenting a message
     * to the customer. If errors make it here they are not fixable at this point and the customer must attempt
     * payment again.
     */
	private async onError(_error: unknown) {
		const {paymentAttempt} = this;
		if (!paymentAttempt) {
			return;
		}

		const message = this.getFriendlyErrorMessage(_error);

		try {
			await paymentAttempt.completeTransaction({note: message});
		} catch {
			// Ignore missing transaction state and still show the customer message.
		}

		paymentAttempt.setPaymentMessage(message);
		paymentAttempt.stopLoading();
	}

	private getFriendlyErrorMessage(error: unknown): string {
		const rawMessage = typeof error === 'object' && error && 'message' in error && typeof error.message === 'string'
			? error.message
			: getErrorString(error);

		switch (rawMessage) {
			case 'INELIGIBLE_CARD_VENDOR':
				return getLocaleText('Your card is not eligible for this payment method. Please try another card.');
			case 'Can not send postrobot_method. Target window is closed':
				return getLocaleText('PayPal card validation was interrupted. Please try again.');
			default:
				return getLocaleText('An error occurred while validating your card. Please check your card number or try another card.');
		}
	}

	/**
     * Called by PayPal when the customer focuses any of the hosted card fields.
     */
	private onFocus(data: HostedFieldEvent) {
		switch (data.emittedBy as unknown as string) {
			case 'number':
			{
				if (data.fields.cardNumberField?.isPotentiallyValid) {
					this.setElementMessage('info', 'Enter your card number.');
				} else {
					this.setElementMessage('error', 'Invalid card number.');
				}

				break;
			}

			case 'expiry':
			{
				if (data.fields.cardExpiryField?.isPotentiallyValid) {
					this.setElementMessage('info', 'Enter your expiration date.');
				} else {
					this.setElementMessage('error', 'Invalid expiration date.');
				}

				break;
			}

			case 'cvv':
			{
				if (data.fields.cardCvvField?.isPotentiallyValid) {
					this.setElementMessage('info', 'Enter the security code.');
				} else {
					this.setElementMessage('error', 'Invalid security code.');
				}

				break;
			}
		}
	}

	/**
     * Called by PayPal when the customer changes any of the hosted card fields.
     */
	private onChange(data: HostedFieldEvent) {
		switch (data.emittedBy as unknown as string) {
			case 'number':
			{
				if (data.fields.cardNumberField?.isPotentiallyValid) {
					this.setElementMessage('info', 'Enter your card number.');
				} else {
					this.setElementMessage('error', 'Invalid card number.');
				}

				if (data.fields.cardNumberField?.isValid) {
					this.setElementMessage('clear');
				}

				break;
			}

			case 'expiry':
			{
				if (data.fields.cardExpiryField?.isPotentiallyValid) {
					this.setElementMessage('info', 'Enter your expiration date.');
				} else {
					this.setElementMessage('error', 'Invalid expiration date.');
				}

				if (data.fields.cardExpiryField?.isValid) {
					this.setElementMessage('clear');
				}

				break;
			}

			case 'cvv':
			{
				if (data.fields.cardCvvField?.isPotentiallyValid) {
					this.setElementMessage('error', 'Enter the security code.');
				} else {
					this.setElementMessage('error', 'Invalid security code.');
				}

				if (data.fields.cardCvvField?.isValid) {
					this.setElementMessage('clear');
				}

				break;
			}
		}
	}

	/**
     * Called by PayPal when the customer blurs any of the hosted card fields.
     */
	private onBlur(data: HostedFieldEvent) {
		if (!data.fields.cardNumberField?.isValid) {
			if (data.fields.cardNumberField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete card number.');
			} else {
				this.setElementMessage('error', 'Invalid card number.');
			}

			return;
		}

		if (!data.fields.cardExpiryField?.isValid) {
			if (data.fields.cardExpiryField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete expiration date.');
			} else {
				this.setElementMessage('error', 'Invalid expiration date.');
			}

			return;
		}

		if (!data.fields.cardCvvField?.isValid) {
			if (data.fields.cardCvvField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete security code.');
			} else {
				this.setElementMessage('error', 'Invalid security code.');
			}

			return;
		}

		this.setElementMessage('clear');
	}

	/**
     * Called by PayPal when the customer attempts to submit(Press enter) in any of the hosted card fields.
     */
	private onInputSubmitRequest(data: HostedFieldEvent) {
		if (!data.fields.cardNumberField?.isValid) {
			if (data.fields.cardNumberField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete card number.');
			} else {
				this.setElementMessage('error', 'Invalid card number.');
			}

			return;
		}

		if (!data.fields.cardExpiryField?.isValid) {
			if (data.fields.cardExpiryField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete expiration date.');
			} else {
				this.setElementMessage('error', 'Invalid expiration date.');
			}

			return;
		}

		if (!data.fields.cardCvvField?.isValid) {
			if (data.fields.cardCvvField?.isPotentiallyValid) {
				this.setElementMessage('error', 'Incomplete security code.');
			} else {
				this.setElementMessage('error', 'Invalid security code.');
			}

			return;
		}

		if (data.isFormValid) {
			this.setElementMessage('clear');
		}
	}

	/**
     * Called by PayPal when the customer attempts to submit(Press enter) in any of the hosted card fields.
     */
	private setElementMessage(type: 'info' | 'error' | 'clear', message?: string) {
		const statusContainer = $qs('#pp-paypal-card-status');
		const statusMessage = $qs('#pp-paypal-card-status span');

		if (!statusContainer || !statusMessage) {
			return;
		}

		if (type === 'clear' || !message) {
			statusContainer.classList.add('hide');
			statusMessage.textContent = '';

			return;
		}

		statusContainer.classList.remove('hide');
		statusMessage.textContent = message;

		if (type === 'info') {
			statusContainer.classList.remove('error');
		} else if (type === 'error') {
			statusContainer.classList.add('error');
		}
	}
}

export {PayPalCardPayment};
